import { deckArtifacts, gameTitle as coreGameTitle, latestVersions, pathPlayed as corePathPlayed, readDeck, type Deck, type ItemMemory } from '@aurora/core'
import type { AuroraState } from './auroraStore'
import { frameworkDomains, frameworkSkill, frameworkSkills } from '../lib/rubric'

/**
 * Derived views over the store.
 *
 * The important shift from the first cut: structure is read OUT of artifacts
 * rather than out of dedicated tables. The agent owns those shapes and may
 * change them, so every reader here is defensive — an unexpected rubric shape
 * degrades to less detail, never to a crash.
 */

const values = <T>(c: Record<string, T> | undefined): T[] => (c ? Object.values(c) : [])
const live = <T extends { deleted?: boolean }>(rows: T[]): T[] => rows.filter((r) => !r?.deleted)

/** Still reading the store off disk — the difference between empty and unknown. */
export function isHydrating(s: AuroraState): boolean {
  return s.ui.hydrated === false
}

export function activeChildId(s: AuroraState): string | undefined {
  return s.ui.activeChildId ?? (s.clientState.activeChildId as string | undefined)
}

export function activeChild(s: AuroraState) {
  const id = activeChildId(s)
  return id ? s.children[id] : undefined
}

function forChild<T extends { childId?: string; deleted?: boolean }>(
  s: AuroraState,
  rows: Record<string, T> | undefined,
): T[] {
  const id = activeChildId(s)
  if (!id) return []
  return live(values(rows)).filter((r) => r.childId === id)
}

// ── what she knows ───────────────────────────────────────────

/** Her memory of every item (a letter, a word, a card), keyed by item: what materials read at launch. */
export function childMemory(s: AuroraState): Record<string, ItemMemory> {
  const out: Record<string, ItemMemory> = {}
  for (const m of forChild(s, (s as any).itemMemory as Record<string, ItemMemory & { childId?: string; deleted?: boolean }>)) out[m.item] = m
  return out
}

// ── threads ──────────────────────────────────────────────────

/**
 * Her threads, newest first. A conversation about a game (its subject is
 * the game) is named for the game as it is now, not the version it started
 * on, so a change that renamed the game renames its conversation too.
 */
export function threads(s: AuroraState) {
  return forChild(s, s.threads)
    .filter((t: any) => !t.archived)
    .sort((a: any, b: any) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0))
    .map((t: any) => (t.artifactId ? { ...t, title: gameTitleNow(s, t.artifactId) ?? t.title } : t))
}

/**
 * The thread the Thread tab shows. With none chosen it opens on the day's
 * conversation: a talk about one game is reached from that game's page or
 * the picker, and must not take over home because it was the last thing said.
 * So a game's talk shows only while it is chosen on this visit to the tab;
 * the one remembered across visits is always a conversation of its own.
 */
export function activeThread(s: AuroraState) {
  const remembered = s.clientState.activeThreadId as string | undefined
  const id = s.ui.activeThreadId ?? (remembered && !(s.threads as any)[remembered]?.artifactId ? remembered : undefined)
  const all = threads(s)
  if (id && s.threads[id]) return all.find((t: any) => t._id === id) ?? s.threads[id]
  return all.find((t: any) => !t.artifactId) ?? all[0]
}

/** A game's name as its newest playable version has it. */
function gameTitleNow(s: AuroraState, id: string): string | undefined {
  return latestVersions(gameVersions(s, id), s.artifacts)[0]?.title
}

export function messagesFor(s: AuroraState, threadId?: string) {
  if (!threadId) return []
  return live(values(s.messages))
    .filter((m: any) => m.threadId === threadId)
    .sort((a: any, b: any) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
}

export function isAgentThinking(s: AuroraState, threadId?: string): boolean {
  return messagesFor(s, threadId).some((m: any) => m.streaming)
}

// ── artifacts ────────────────────────────────────────────────

export function artifacts(s: AuroraState) {
  return forChild(s, s.artifacts).sort(
    (a: any, b: any) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
  )
}

export function artifactsOfKind(s: AuroraState, kind: string) {
  return artifacts(s).filter((a: any) => a.kind === kind)
}

/** The artifact the UI should treat as authoritative for a kind. */
export function currentArtifact(s: AuroraState, kind: string) {
  const of = artifactsOfKind(s, kind)
  return of.find((a: any) => a.isCurrent !== false) ?? of[0]
}

export function currentRubric(s: AuroraState) {
  return currentArtifact(s, 'rubric')
}

export function currentPlan(s: AuroraState) {
  return currentArtifact(s, 'plan')
}

/** Her own work — the gallery, deliberately excluding generated material. */
export function childWork(s: AuroraState) {
  return artifacts(s).filter(
    (a: any) => a.source === 'child' || a.kind === 'photo' || a.kind === 'drawing',
  )
}

export function activities(s: AuroraState) {
  return artifacts(s).filter((a: any) => a.kind === 'activity')
}

/** Every kind currently present, for filter chips. Open by construction. */
export function artifactKinds(s: AuroraState): Array<{ kind: string; count: number }> {
  const counts = new Map<string, number>()
  for (const a of artifacts(s)) {
    counts.set((a as any).kind, (counts.get((a as any).kind) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => b.count - a.count)
}

// ── plan (read out of the plan artifact) ─────────────────────

// ── people ───────────────────────────────────────────────────

export interface Member { _id: string; name?: string; label?: string; email?: string; image?: string }

export function members(s: AuroraState): Member[] {
  return Object.values((s as any).members ?? {}) as Member[]
}

/**
 * How a parent is named in the interface: their role when they chose one
 * ("Mom"), else their first name, else their email. Unknown ids read as
 * nothing rather than as a wrong person.
 */
export function memberName(s: AuroraState, id?: string): string | undefined {
  if (!id) return undefined
  const m = ((s as any).members ?? {})[id] as Member | undefined
  if (!m) return undefined
  const label = m.label?.trim()
  if (label) return label.charAt(0).toUpperCase() + label.slice(1)
  return m.name?.trim().split(/\s+/)[0] || m.email
}

/** The parent behind this session, from the account fetched at boot. */
export function meId(s: AuroraState): string | undefined {
  return (s.account as any)?.user_id ?? undefined
}

/** "You" for the viewer, else the member's name. */
export function authorName(s: AuroraState, id?: string): string | undefined {
  if (!id) return undefined
  return id === meId(s) ? 'You' : memberName(s, id)
}

export interface PlanBlock {
  id: string
  title: string
  instructions?: string
  why?: string
  minutes?: number
  domain?: string
  /** Display name ("Gross Motor"); `domain` is the key ("grossMotor"). */
  domainName?: string
  status?: string
  /** The Notes column of the paper plan. */
  note?: string
  /** The sign it is working, and what to have in hand. */
  watchFor?: string
  materials?: string[]
  /** The parent behind the latest mark or note. */
  authorId?: string
  completedAt?: number
  artifactIds?: string[]
  /** Free text, matched against rubric skill names where it can be. */
  skills?: string[]
}

/** One synced block row as a card. */
export function toPlanBlock(b: any): PlanBlock {
  return {
    id: b._id,
    title: b.title ?? 'Activity',
    instructions: b.instructions,
    why: b.why,
    minutes: typeof b.minutes === 'number' ? b.minutes : 10,
    domain: b.domainKey ?? b.domain_key,
    domainName: b.domainName ?? b.domain_name,
    status: b.status ?? 'pending',
    note: b.note,
    watchFor: b.watchFor ?? b.watch_for,
    materials: b.materials ?? [],
    authorId: b.authorId ?? b.author_id,
    completedAt: b.completedAt ?? b.completed_at,
    artifactIds: b.artifactIds ?? b.artifact_ids ?? [],
    skills: b.skillKeys ?? b.skill_keys ?? [],
  }
}

/**
 * Pull one day's blocks out of whatever shape the plan artifact has.
 *
 * Accepts several plausible shapes because the agent authored it and may well
 * pick a different one next time. Unknown shape means an empty day, which the
 * UI already handles gracefully.
 */
export function blocksForDate(s: AuroraState, date: string): PlanBlock[] {
  // The spine is the primary source: block rows sync granularly, check off
  // offline, and survive plan revisions (write_plan never touches settled rows).
  const spine = forChild(s, s.blocks)
    .filter((b: any) => b.date === date)
    .sort((a: any, b: any) => (a.slot ?? 0) - (b.slot ?? 0))
  if (spine.length) {
    return spine.map(toPlanBlock)
  }

  const plan: any = currentPlan(s)
  const data = plan?.data
  if (!data) return []

  const days: any[] =
    (Array.isArray(data.days) && data.days) ||
    (Array.isArray(data.weeks) && data.weeks.flatMap((w: any) => w?.days ?? [])) ||
    []

  const day = days.find((d) => d?.date === date)
  const raw: any[] = day?.blocks ?? []

  return raw.map((b, i) => ({
    id: b?.id ?? `${date}:${i}`,
    title: b?.title ?? 'Activity',
    instructions: b?.instructions ?? b?.what,
    why: b?.why ?? b?.rationale,
    minutes: typeof b?.minutes === 'number' ? b.minutes : (b?.estimatedMinutes ?? 10),
    domain: b?.domain ?? b?.domainKey,
    status: b?.status ?? 'pending',
    artifactIds: b?.artifactIds ?? [],
    skills: b?.skills ?? b?.skillIds ?? [],
  }))
}

// ── cycle structure ──────────────────────────────────────────

export interface PlanDay {
  date: string
  note?: string
  /** The day's woven-in caregiving moment, from the plan. */
  caregiving?: string
  blocks: PlanBlock[]
  ritualsDone: string[]
}

export interface PlanWeek {
  /** Spine row id, when the week is a row. */
  id?: string
  number: number
  theme?: string
  materials: string[]
  focus: Array<{ domain: string; statement: string }>
  /** The week's own check-in questions. */
  checkinQuestions?: string[]
  days: PlanDay[]
  startDate?: string
  endDate?: string
}

export interface PlanGoal {
  domain?: string
  statement: string
}

/** The plan artifact's body, or an empty object. Never null, so callers stay flat. */
function planData(s: AuroraState): any {
  return (currentPlan(s) as any)?.data ?? {}
}

/** The active cycle row on the spine, if any. */
export function activeCycle(s: AuroraState) {
  return forChild(s, s.cycles).find((c: any) => c.status === 'active')
}

export function planTitle(s: AuroraState): string | undefined {
  const cycle: any = activeCycle(s)
  if (cycle?.title) return cycle.title
  return planData(s).title ?? (currentPlan(s) as any)?.title
}

export function planGoals(s: AuroraState): PlanGoal[] {
  const cycle: any = activeCycle(s)
  if (Array.isArray(cycle?.goals) && cycle.goals.length) {
    return cycle.goals
      .map((g: any) => ({
        domain: g?.domainKey ?? g?.domain_key,
        statement: g?.statement ?? '',
      }))
      .filter((g: PlanGoal) => g.statement)
  }
  const raw = planData(s).goals
  if (!Array.isArray(raw)) return []
  return raw
    .map((g: any) =>
      typeof g === 'string'
        ? { statement: g }
        : { domain: g?.domain ?? g?.domainKey, statement: g?.statement ?? g?.goal ?? '' },
    )
    .filter((g) => g.statement)
}

export function planPriorities(s: AuroraState): string[] {
  const raw = planData(s).priorities
  return Array.isArray(raw) ? raw.filter((p: any) => typeof p === 'string') : []
}

/** Constraints the plan was written under — the agent's own reasons, shown back. */
export function planPrinciples(s: AuroraState): string[] {
  const cycle: any = activeCycle(s)
  if (Array.isArray(cycle?.principles) && cycle.principles.length) return cycle.principles
  const raw = planData(s).principles
  return Array.isArray(raw) ? raw.filter((p: any) => typeof p === 'string') : []
}

function addDaysIso(iso: string, days: number): string {
  if (!iso) return iso
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

/**
 * Normalize the plan into weeks of days.
 *
 * A plan with a bare `days` array is treated as a single unnumbered week, so a
 * quick one-week plan and a full six-week cycle render through the same path.
 */
export function planWeeks(s: AuroraState): PlanWeek[] {
  // Spine rows first: weeks + days + blocks compose the same PlanWeek shape,
  // so every consumer downstream is source-agnostic.
  const spineWeeks = forChild(s, s.weeks)
  if (spineWeeks.length) {
    const cycle = activeCycle(s)
    const days = forChild(s, s.days)
    const blocks = forChild(s, s.blocks)
    return (spineWeeks as any[])
      .filter((w) => !cycle || w.cycleId === (cycle as any)._id || w.cycle_id === (cycle as any)._id)
      .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
      .map((w) => {
        const start = w.startDate ?? w.start_date ?? ''
        const end = addDaysIso(start, 6)
        const weekDays: PlanDay[] = (days as any[])
          .filter((d) => d.date >= start && d.date <= end)
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((d) => ({
            date: d.date,
            note: d.note,
            caregiving: d.caregiving,
            ritualsDone: [],
            blocks: blocksForDate(s, d.date),
          }))
        return {
          id: w._id,
          number: w.number ?? 1,
          theme: w.theme,
          materials: w.materialsList ?? w.materials_list ?? [],
          focus: w.focus ? [{ domain: '', statement: String(w.focus) }] : [],
          checkinQuestions: w.checkinQuestions ?? w.checkin_questions ?? [],
          days: weekDays,
          startDate: start || undefined,
          endDate: weekDays.length ? weekDays[weekDays.length - 1]!.date : end,
        }
      })
  }

  const data = planData(s)
  const rawWeeks: any[] = Array.isArray(data.weeks)
    ? data.weeks
    : Array.isArray(data.days)
      ? [{ number: 1, days: data.days }]
      : []

  return rawWeeks.map((w, i) => {
    const days: PlanDay[] = (Array.isArray(w?.days) ? w.days : []).map((d: any, j: number) => ({
      date: d?.date ?? '',
      note: d?.note,
      ritualsDone: Array.isArray(d?.ritualsDone) ? d.ritualsDone : [],
      blocks: (Array.isArray(d?.blocks) ? d.blocks : []).map((b: any, k: number) => ({
        id: b?.id ?? `${d?.date ?? i}:${j}:${k}`,
        title: b?.title ?? 'Activity',
        instructions: b?.instructions ?? b?.what,
        why: b?.why ?? b?.rationale,
        minutes: typeof b?.minutes === 'number' ? b.minutes : (b?.estimatedMinutes ?? 10),
        domain: b?.domain ?? b?.domainKey,
        status: b?.status ?? 'pending',
        artifactIds: b?.artifactIds ?? [],
        skills: b?.skills ?? b?.skillIds ?? [],
      })),
    }))

    const dated = days.map((d) => d.date).filter(Boolean).sort()

    return {
      number: typeof w?.number === 'number' ? w.number : i + 1,
      theme: w?.theme,
      materials: Array.isArray(w?.materialsList)
        ? w.materialsList
        : Array.isArray(w?.materials)
          ? w.materials
          : [],
      focus: Object.entries(w?.focusByDomain ?? w?.focus ?? {}).map(([domain, statement]) => ({
        domain,
        statement: String(statement),
      })),
      days,
      startDate: w?.startDate ?? dated[0],
      endDate: dated[dated.length - 1],
    }
  })
}

/**
 * A plan the agent has drafted but nobody has adopted yet.
 *
 * The next cycle arrives as a proposal to accept or argue with, never as a fact
 * — so it is deliberately not the current plan until a parent says so.
 */
export function proposedPlan(s: AuroraState) {
  return artifactsOfKind(s, 'plan').find(
    (a: any) =>
      a._id !== (currentPlan(s) as any)?._id &&
      (a.status === 'draft' || a.data?.status === 'draft' || a.tags?.includes('proposal')),
  )
}

export function weekForDate(s: AuroraState, date: string): PlanWeek | undefined {
  return planWeeks(s).find((w) => w.days.some((d) => d.date === date))
}

/** The week to open by default: the one containing today, else the first. */
export function currentWeek(s: AuroraState, today: string): PlanWeek | undefined {
  const weeks = planWeeks(s)
  return weeks.find((w) => w.days.some((d) => d.date === today)) ?? weeks[0]
}

// ── rituals ──────────────────────────────────────────────────

export interface Ritual {
  key: string
  title: string
  description: string
  minutes?: number
  done: boolean
}

/**
 * Rituals are woven into normal life rather than scheduled, so they live at
 * plan level and are checked off per day. These three are the standing set the
 * plan can override or replace entirely.
 */
const DEFAULT_RITUALS = [
  {
    key: 'calendar',
    title: 'Calendar and time',
    description: 'Mark the day off together. How many sleeps until the thing she is waiting for?',
    minutes: 2,
  },
  {
    key: 'writeAndLabel',
    title: 'Write and label',
    description: 'She draws anyway, so ask her to write one word on it. Letters ride on drawing.',
    minutes: 3,
  },
  {
    key: 'caregiving',
    title: 'Caregiving',
    description: 'Read to a doll, the baby, or you. Teaching someone else holds her attention longest.',
    minutes: 5,
  },
]

export function ritualsForDate(s: AuroraState, date: string): Ritual[] {
  const raw = planData(s).rituals
  const defs = Array.isArray(raw) && raw.length ? raw : DEFAULT_RITUALS
  const day = planWeeks(s)
    .flatMap((w) => w.days)
    .find((d) => d.date === date)
  const done = new Set(day?.ritualsDone ?? [])
  const caregiving = dayRow(s, date)?.caregiving as string | undefined

  return defs.map((r: any, i: number) => ({
    key: r?.key ?? String(i),
    title: r?.title ?? 'Ritual',
    description:
      caregiving && /caregiv/i.test(String(r?.key ?? r?.title ?? ''))
        ? caregiving
        : (r?.description ?? ''),
    minutes: typeof r?.minutes === 'number' ? r.minutes : r?.estimatedMinutes,
    done: done.has(r?.key ?? String(i)),
  }))
}

// ── progress ─────────────────────────────────────────────────

export interface DayProgress {
  total: number
  settled: number
  done: number
  minutesLeft: number
  allSettled: boolean
}

export function dayProgress(s: AuroraState, date: string): DayProgress {
  const blocks = blocksForDate(s, date)
  const settled = blocks.filter((b) => b.status && b.status !== 'pending')
  return {
    total: blocks.length,
    settled: settled.length,
    done: blocks.filter((b) => b.status === 'done').length,
    minutesLeft: blocks
      .filter((b) => !b.status || b.status === 'pending')
      .reduce((sum, b) => sum + (b.minutes ?? 10), 0),
    allSettled: blocks.length > 0 && settled.length === blocks.length,
  }
}

export interface CycleProgress {
  weeks: number
  weeksElapsed: number
  days: number
  daysWithBlocks: number
  blocks: number
  blocksSettled: number
}

export function cycleProgress(s: AuroraState, today: string): CycleProgress {
  const weeks = planWeeks(s)
  const days = weeks.flatMap((w) => w.days)
  const blocks = days.flatMap((d) => d.blocks)
  return {
    weeks: weeks.length,
    weeksElapsed: weeks.filter((w) => (w.startDate ?? '') <= today).length,
    days: days.length,
    daysWithBlocks: days.filter((d) => d.blocks.length > 0).length,
    blocks: blocks.length,
    blocksSettled: blocks.filter((b) => b.status && b.status !== 'pending').length,
  }
}

/**
 * Evidence gathered against each goal.
 *
 * Deliberately a count of moments, not a percentage: a goal is not something
 * you are 60% of the way through, and a number that looks like a score invites
 * exactly the reading this product refuses.
 */
export function goalEvidence(s: AuroraState): Array<{ goal: PlanGoal; moments: number }> {
  const caps = captures(s) as any[]
  return planGoals(s).map((goal) => ({
    goal,
    moments: goal.domain
      ? caps.filter((c) => (c.interpretation?.domains ?? []).includes(goal.domain)).length
      : 0,
  }))
}

export function planDayNote(s: AuroraState, date: string): string | undefined {
  const data: any = (currentPlan(s) as any)?.data
  const days: any[] = data?.days ?? []
  return days.find((d) => d?.date === date)?.note
}

export function planTheme(s: AuroraState, date: string): string | undefined {
  const data: any = (currentPlan(s) as any)?.data
  if (!data) return undefined
  if (Array.isArray(data.weeks)) {
    for (const w of data.weeks) {
      if ((w?.days ?? []).some((d: any) => d?.date === date)) return w?.theme
    }
  }
  return data.theme
}

// ── rubric (read out of the rubric artifact) ─────────────────

export interface RubricSkill {
  key: string
  name: string
  level?: number
  confidence?: number
  trend?: string
  note?: string
  evidence?: string[]
  /** Set by the parent. Always wins, and the UI must always say so. */
  parentOverride?: { level: number; note?: string; at: number; inferredLevel?: number }
}

export interface RubricDomain {
  key: string
  name: string
  skills: RubricSkill[]
  level: number
  observedCount: number
  trend: string
}

export function rubricDomains(s: AuroraState): RubricDomain[] {
  const spine = rubricDomainsFromSpine(s)
  if (spine.length) return spine
  return rubricDomainsFrom((currentRubric(s) as any)?.data)
}

/**
 * The spine is the primary source: skills rows define the axes, skill_states
 * rows say where the child stands. The artifact path below remains as a
 * fallback so an agent-authored rubric document still renders.
 */
export function rubricDomainsFromSpine(s: AuroraState): RubricDomain[] {
  const skills = forChild(s, s.skills)
  if (!skills.length) return []

  const stateByKey = new Map(
    forChild(s, s.skillStates).map((st: any) => [st.skillKey ?? st.skill_key, st]),
  )

  const byDomain = new Map<string, { name: string; skills: RubricSkill[]; order: number }>()
  for (const sk of skills as any[]) {
    if (sk.archived) continue
    const dk = sk.domainKey ?? sk.domain_key ?? (sk.key ?? '').split('.')[0] ?? 'other'
    if (!byDomain.has(dk)) {
      byDomain.set(dk, {
        name: sk.domainName ?? sk.domain_name ?? dk,
        skills: [],
        order: sk.sortOrder ?? sk.sort_order ?? 0,
      })
    }
    const st: any = stateByKey.get(sk.key)
    byDomain.get(dk)!.skills.push({
      key: sk.key,
      name: sk.name,
      level: typeof st?.level === 'number' && (st?.observationCount ?? st?.observation_count ?? 0) > 0
        ? st.level
        : typeof st?.level === 'number' && (st?.confidence ?? 0) > 0.35
          ? st.level
          : undefined,
      confidence: st?.confidence,
      trend: st?.trend,
      note: st?.note,
      evidence: st?.evidenceCaptureIds ?? st?.evidence_capture_ids,
      parentOverride: st?.parentOverride ?? st?.parent_override,
    })
  }

  return [...byDomain.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, d]) => {
      const observed = d.skills.filter((sk) => typeof sk.level === 'number')
      const level = observed.length
        ? observed.reduce((sum, sk) => sum + (sk.level as number), 0) / observed.length
        : 0
      const trend = observed.some((sk) => sk.trend === 'regressing')
        ? 'regressing'
        : observed.some((sk) => sk.trend === 'stalled')
          ? 'stalled'
          : observed.some((sk) => sk.trend === 'rising')
            ? 'rising'
            : 'steady'
      return { key, name: d.name, skills: d.skills, level, observedCount: observed.length, trend }
    })
}

/** The same reading, against any rubric body — used to diff two points in time. */
export function rubricDomainsFrom(data: any): RubricDomain[] {
  const domains: any[] = Array.isArray(data?.domains) ? data.domains : []

  return domains.map((d) => {
    const skills: RubricSkill[] = (Array.isArray(d?.skills) ? d.skills : []).map((sk: any) => ({
      key: sk?.key ?? sk?.name ?? '',
      name: sk?.name ?? sk?.key ?? '',
      level: typeof sk?.level === 'number' ? sk.level : undefined,
      confidence: typeof sk?.confidence === 'number' ? sk.confidence : undefined,
      trend: sk?.trend,
      note: sk?.note,
      evidence: Array.isArray(sk?.evidence) ? sk.evidence : undefined,
      parentOverride: sk?.parentOverride,
    }))

    // Unobserved skills are excluded rather than counted as zero — otherwise a
    // domain looks worse the more finely it is subdivided.
    const observed = skills.filter((sk) => typeof sk.level === 'number')
    const level = observed.length
      ? observed.reduce((sum, sk) => sum + (sk.level as number), 0) / observed.length
      : 0

    const trend = observed.some((sk) => sk.trend === 'regressing')
      ? 'regressing'
      : observed.some((sk) => sk.trend === 'stalled')
        ? 'stalled'
        : observed.some((sk) => sk.trend === 'rising')
          ? 'rising'
          : 'steady'

    return {
      key: d?.key ?? d?.name ?? '',
      name: d?.name ?? d?.key ?? '',
      skills,
      level,
      observedCount: observed.length,
      trend,
    }
  })
}

/**
 * Every rubric ever written, oldest first.
 *
 * The ceremony needs two points in time, and the only honest source for "where
 * she was" is the rubric that was current back then.
 */
export function rubricHistory(s: AuroraState) {
  return artifactsOfKind(s, 'rubric').sort(
    (a: any, b: any) => (a.createdAt ?? 0) - (b.createdAt ?? 0),
  )
}

export interface SkillMove {
  domain: string
  name: string
  from?: number
  to?: number
  /** Positive is growth; 0 means held steady; undefined means newly looked at. */
  delta?: number
  note?: string
  trend?: string
}

/**
 * What moved between two rubrics.
 *
 * A skill present only in the later rubric counts as newly observed, not as a
 * gain from zero — the difference matters, because zero was never true.
 */
export function rubricMoves(before: any, after: any): SkillMove[] {
  const past = new Map<string, RubricSkill>()
  for (const d of rubricDomainsFrom(before)) {
    for (const sk of d.skills) past.set(`${d.key}:${sk.key}`, sk)
  }

  const out: SkillMove[] = []
  for (const d of rubricDomainsFrom(after)) {
    for (const sk of d.skills) {
      const was = past.get(`${d.key}:${sk.key}`)
      out.push({
        domain: d.name,
        name: sk.name,
        from: was?.level,
        to: sk.level,
        delta:
          typeof sk.level === 'number' && typeof was?.level === 'number'
            ? sk.level - was.level
            : undefined,
        note: sk.note,
        trend: sk.trend,
      })
    }
  }
  return out
}

export interface DomainSummary {
  key: string
  name: string
  skills: RubricSkill[]
  /** Mean level across observed skills only. 0 when nothing is observed. */
  level: number
  /** Mean confidence across observed skills, 0..1. */
  confidence: number
  trend: string
  observedCount: number
  skillCount: number
  /** How many captures have touched this domain. */
  evidenceCount: number
  lastObservedAt?: number
  /** False when the agent invented a domain the framework has never heard of. */
  inFramework: boolean
}

/**
 * Every domain, whether or not the rubric has anything to say about it.
 *
 * The framework's thirteen are always present so the grid is a stable map of
 * the territory rather than a list that grows as observations arrive. A domain
 * nobody has looked at yet carries `observedCount: 0`, which the UI must render
 * as "not looked at yet" — never as a level of zero.
 */
export function domainSummaries(s: AuroraState): DomainSummary[] {
  const fromRubric = new Map(rubricDomains(s).map((d) => [d.key, d]))
  const caps = captures(s) as any[]

  const build = (key: string, name: string, inFramework: boolean): DomainSummary => {
    const d = fromRubric.get(key)
    const skills = d?.skills ?? []
    const observed = skills.filter((sk) => typeof sk.level === 'number')
    const withConfidence = observed.filter((sk) => typeof sk.confidence === 'number')
    const touching = caps.filter((c) => readInterpretation(c)?.domains.includes(key))

    return {
      key,
      name: d?.name ?? name,
      skills,
      level: d?.level ?? 0,
      confidence: withConfidence.length
        ? withConfidence.reduce((sum, sk) => sum + (sk.confidence as number), 0) /
          withConfidence.length
        : 0,
      trend: d?.trend ?? 'steady',
      observedCount: observed.length,
      skillCount: skills.length || frameworkSkills(key).length,
      evidenceCount: touching.length,
      lastObservedAt: touching.length ? Math.max(...touching.map((c) => c.occurredAt ?? 0)) : undefined,
      inFramework,
    }
  }

  const out = frameworkDomains().map((d) => build(d.key, d.name, true))
  const known = new Set(out.map((d) => d.key))
  for (const d of fromRubric.values()) {
    if (d.key && !known.has(d.key)) out.push(build(d.key, d.name, false))
  }
  return out
}

export function domainSummary(s: AuroraState, key?: string): DomainSummary | undefined {
  if (!key) return undefined
  return domainSummaries(s).find((d) => d.key === key)
}

/** One skill as the rubric currently holds it. */
export function rubricSkill(
  s: AuroraState,
  domainKey?: string,
  skillKey?: string,
): RubricSkill | undefined {
  if (!skillKey) return undefined
  return domainSummary(s, domainKey)?.skills.find((sk) => sk.key === skillKey)
}

// ── evidence ─────────────────────────────────────────────────

export interface EvidenceItem {
  captureId: string
  at: number
  summary: string
  /** What the observation showed: demonstrated, partial, struggled, refused. */
  signal?: string
  /** Where the agent read her, when it said. */
  suggestedLevel?: number
  /** How she seemed. Mood, deliberately kept apart from performance. */
  affect?: string
  quote?: string
  /** What was actually said or typed, kept so a number can always be audited. */
  text?: string
  source?: string
  /**
   * True when this observation was cited for this specific skill. False when we
   * are showing it because it touched the same domain — a weaker claim, and the
   * UI says so rather than passing it off as direct evidence.
   */
  direct: boolean
}

/** Does this interpretation name the skill we are asking about? */
function touchFor(
  interp: Interpretation | null,
  skillKey?: string,
  skillName?: string,
): SkillTouch | undefined {
  if (!interp || !skillKey) return undefined
  const wanted = [skillKey.toLowerCase(), (skillName ?? '').toLowerCase()].filter(Boolean)
  return interp.skills.find((touch) => {
    const name = touch.name.toLowerCase()
    // Ids arrive as either "blending" or "literacy.blending".
    const tail = name.slice(name.lastIndexOf('.') + 1)
    return wanted.some((w) => name === w || tail === w)
  })
}

/**
 * The observations behind a level.
 *
 * This is the whole reason a parent should believe a number. Three ways in,
 * strongest first: an explicit citation on the skill, an interpretation that
 * names the skill, or — last and clearly labelled — anything logged in the same
 * domain. Dropping that third route would leave the trail empty far too often,
 * and an empty trail teaches a parent that the numbers are unaccountable.
 */
export function evidenceFor(
  s: AuroraState,
  domainKey?: string,
  skillKey?: string,
): EvidenceItem[] {
  const caps = captures(s) as any[]
  if (!caps.length) return []

  const skill = skillKey ? rubricSkill(s, domainKey, skillKey) : undefined
  const cited = new Set(skill?.evidence ?? [])
  const skillName = frameworkSkill(domainKey, skillKey)?.name ?? skill?.name

  const read = caps.map((c) => ({ capture: c, interp: readInterpretation(c) }))

  const toItem = (
    { capture, interp }: { capture: any; interp: Interpretation | null },
    direct: boolean,
  ): EvidenceItem => {
    const touch = touchFor(interp, skillKey, skillName)
    return {
      captureId: capture._id,
      at: capture.occurredAt ?? capture.capturedAt ?? 0,
      summary: interp?.summary ?? '',
      signal: touch?.signal,
      suggestedLevel: touch?.suggestedLevel,
      affect: interp?.affect,
      quote: interp?.quotes[0],
      text: capture.raw?.transcript ?? capture.raw?.text,
      source: capture.source,
      direct,
    }
  }

  const seen = new Set<string>()
  const out: EvidenceItem[] = []
  const take = (rows: typeof read, direct: boolean) => {
    for (const row of rows) {
      if (seen.has(row.capture._id)) continue
      seen.add(row.capture._id)
      out.push(toItem(row, direct))
    }
  }

  take(read.filter((r) => cited.has(r.capture._id)), true)
  take(read.filter((r) => touchFor(r.interp, skillKey, skillName)), true)
  take(
    read.filter((r) => !!domainKey && !!r.interp?.domains.includes(domainKey)),
    false,
  )

  return out.sort((a, b) => b.at - a.at)
}

/** Stuck despite real effort — the most decision-relevant thing on the screen. */
export function stalledSkills(s: AuroraState) {
  const out: Array<{ domain: string; skill: RubricSkill }> = []
  for (const d of rubricDomains(s)) {
    for (const sk of d.skills) {
      if (sk.trend === 'stalled') out.push({ domain: d.name, skill: sk })
    }
  }
  return out
}

// ── captures ─────────────────────────────────────────────────

export function captures(s: AuroraState) {
  return forChild(s, s.captures).sort(
    (a: any, b: any) => (b.occurredAt ?? 0) - (a.occurredAt ?? 0),
  )
}

export function capturesInRange(s: AuroraState, from: number, to: number) {
  return captures(s).filter((c: any) => c.occurredAt >= from && c.occurredAt <= to)
}

/**
 * What the agent made of one capture.
 *
 * The agent authors this and will reshape it, so every field is read from
 * several plausible names and anything unrecognised simply goes missing. A
 * capture whose interpretation arrives in an unexpected shape shows less
 * detail; it never shows an error, and it is never dropped.
 */
export interface SkillTouch {
  /** Display name. Falls back to whatever identifier is present. */
  name: string
  /** demonstrated · partial · struggled · refused, when stated. */
  signal?: string
  /** Where the agent thinks the level should move to. */
  suggestedLevel?: number
  /** Where it stands today, when the agent says. */
  currentLevel?: number
}

export interface Interpretation {
  summary?: string
  domains: string[]
  quotes: string[]
  skills: SkillTouch[]
  affect?: string
}

const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : [])
const asNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

function readSkill(raw: any): SkillTouch | null {
  if (typeof raw === 'string') return raw.trim() ? { name: raw.trim() } : null
  if (!raw || typeof raw !== 'object') return null
  const name = raw.name ?? raw.skill ?? raw.skillName ?? raw.key ?? raw.skillId
  if (!name) return null
  return {
    name: String(name),
    signal: raw.signal ?? raw.evidence ?? undefined,
    suggestedLevel: asNumber(raw.suggestedLevel ?? raw.toLevel ?? raw.to ?? raw.level),
    currentLevel: asNumber(raw.currentLevel ?? raw.fromLevel ?? raw.from),
  }
}

/** Normalise one capture's interpretation. Returns null before it arrives. */
export function readInterpretation(capture: any): Interpretation | null {
  const raw = capture?.interpretation
  if (!raw || typeof raw !== 'object') return null

  return {
    summary: typeof raw.summary === 'string' ? raw.summary : undefined,
    domains: asArray(raw.domains ?? raw.domainKeys)
      .filter((d) => typeof d === 'string' && d.trim())
      .map((d) => d.trim()),
    quotes: asArray(raw.quotes ?? raw.childQuotes)
      .filter((q) => typeof q === 'string' && q.trim())
      .map((q) => q.trim()),
    skills: asArray(raw.skills ?? raw.skillEvidence)
      .map(readSkill)
      .filter((sk): sk is SkillTouch => sk !== null),
    affect: typeof raw.affect === 'string' ? raw.affect : undefined,
  }
}

/** The child's own words. The emotional core of the Story view. */
export function childQuotes(s: AuroraState) {
  const out: Array<{ quote: string; at: number; captureId: string }> = []
  for (const c of captures(s) as any[]) {
    for (const quote of readInterpretation(c)?.quotes ?? []) {
      out.push({ quote, at: c.occurredAt, captureId: c._id })
    }
  }
  return out.sort((a, b) => b.at - a.at)
}

export function isInterpreting(s: AuroraState, captureId: string): boolean {
  return s.capture.interpreting.includes(captureId)
}

// ── memory ───────────────────────────────────────────────────

export function memories(s: AuroraState) {
  return forChild(s, s.memories)
    .filter((m: any) => m.status === 'active')
    .sort((a: any, b: any) => (b.weight ?? 0) - (a.weight ?? 0))
}

export function principles(s: AuroraState) {
  return memories(s).filter((m: any) => m.always || m.kind === 'principle')
}

export function interests(s: AuroraState) {
  return memories(s).filter((m: any) => m.kind === 'interest')
}

// ── materials (generated interactives) ───────────────────────

/**
 * The spec out of a material artifact.
 *
 * The agent has stored it under more than one key across versions, so read all
 * of them rather than pinning the UI to whichever one shipped first. Returns
 * unknown on purpose: nothing may treat this as a spec until `validateSpec`
 * has said so.
 */
export function materialSpec(artifact: any): unknown {
  return artifact?.spec ?? artifact?.generation?.spec ?? artifact?.data?.spec ?? artifact?.data
}

export function materialKind(artifact: any): string | undefined {
  const spec = materialSpec(artifact) as any
  if (typeof spec?.kind === 'string') return spec.kind
  const requested = artifact?.generation?.requestedKind as string | undefined
  if (requested) return requested
  return typeof artifact?.html === 'string' && artifact.html.trim() ? 'activity' : undefined
}

export function materialSkillIds(artifact: any): string[] {
  const ids = (materialSpec(artifact) as any)?.targetSkillIds
  return Array.isArray(ids) ? ids.filter((id: unknown) => typeof id === 'string') : []
}

/**
 * Human names for the skill ids a spec targets, read out of the rubric the
 * agent authored. An id with no entry there keeps its own name rather than
 * being hidden — a filter that silently drops options is worse than an ugly one.
 */
export function skillLabels(s: AuroraState): Record<string, string> {
  const out: Record<string, string> = {}
  for (const domain of rubricDomains(s)) {
    for (const skill of domain.skills) {
      if (skill.key) out[skill.key] = skill.name || skill.key
    }
  }
  return out
}

/** Skill key → its domain key, so a material can wear its domain's colour. */
export function skillDomainKeys(s: AuroraState): Record<string, string> {
  const out: Record<string, string> = {}
  for (const domain of rubricDomains(s)) {
    for (const skill of domain.skills) {
      if (skill.key && domain.key) out[skill.key] = domain.key
    }
  }
  return out
}

// ── the playroom ─────────────────────────────────────────────

/**
 * Every game made for her, from an activity card, the Playroom or the
 * agent: one per version chain, newest first, whether it is being made,
 * ready or failed. Drafts are here too; nothing a parent asked for is
 * hidden. The reading path's own games live on the path instead.
 */
export function madeGames(s: AuroraState) {
  return latestVersions(
    forChild(s, s.artifacts).filter((a: any) => a.kind === 'activity' && a.source !== 'path'),
    s.artifacts,
  )
}

/**
 * Every version of a game, oldest first: the rows core puts in one chain
 * with it (a revision supersedes the version it changed).
 */
export function gameVersions(s: AuroraState, id?: string) {
  const it = id ? s.artifacts[id] : undefined
  if (!it) return []
  return live(values(s.artifacts))
    .filter((a: any) => a._id === id || (a.childId === it.childId && latestVersions([a, it], s.artifacts).length === 1))
    .sort((a: any, b: any) => (a.version ?? 0) - (b.version ?? 0) || (a.createdAt ?? 0) - (b.createdAt ?? 0))
}

/**
 * The version of this game that plays now (the newest ready one), wherever
 * an older version is referenced from: a card posted with version 2 must not
 * hand her version 2 once version 4 exists.
 */
export function currentVersion(s: AuroraState, id?: string) {
  return latestVersions(gameVersions(s, id))[0]
}

/** A newer version of this game being made or failed, if the agent is changing it. */
export function gameRevision(s: AuroraState, id: string) {
  return live(values(s.artifacts))
    .filter((a: any) => a.supersedes === id && a.generation?.status && a.generation.status !== 'ready')
    .sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0]
}

/**
 * What a game is called on screen (core gameTitle): a change being made
 * wears the name of the version that plays now, a new game for an activity
 * is that activity's game.
 */
export function gameTitle(s: AuroraState, a: any): string {
  return coreGameTitle(a, {
    playing: a.supersedes ? currentVersion(s, a._id)?.title : undefined,
    activity: a.blockRef ? (s.blocks as any)[a.blockRef]?.title : undefined,
  })
}

/**
 * Her row for each reading-path step, as pathStanding reads it: core's
 * pathPlayed, so a book rewritten for her, a step taken off her path and a
 * level the parent placed her at read the same here, on the phone and to
 * the agent.
 */
export function pathPlayed(s: AuroraState) {
  return corePathPlayed(s.artifacts as any, activeChildId(s))
}

/** Her decks whole enough to play: what makes a word she reads on a card count on the path and in What she knows. */
export function childDecks(s: AuroraState): Deck[] {
  return deckArtifacts(artifactsOfKind(s, 'deck') as any[]).map((a) => readDeck(a)).filter((d): d is Deck => !!d)
}

/** The conversation about a game: the thread whose subject is any of its versions. */
export function threadAbout(s: AuroraState, ids: string[]) {
  const wanted = new Set(ids)
  return forChild(s, s.threads)
    .filter((t: any) => t.artifactId && wanted.has(t.artifactId))
    .sort((a: any, b: any) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0))[0]
}

/** Today's pending blocks, for the "attach to a block" picker. */
export function attachableBlocks(s: AuroraState, date: string): PlanBlock[] {
  return blocksForDate(s, date).filter((b) => !b.status || b.status === 'pending')
}


// ── the loop: history, reviews, targets ──────────────────────

export interface LevelPoint {
  at: number
  level: number
  source?: string
  note?: string
}

/** Level movements for one skill, oldest first. */
export function levelHistoryFor(s: AuroraState, skillKey: string): LevelPoint[] {
  return forChild(s, s.levelHistory)
    .filter((h: any) => (h.skillKey ?? h.skill_key) === skillKey)
    .map((h: any) => ({ at: h.at, level: h.level, source: h.source, note: h.note }))
    .sort((a, b) => a.at - b.at)
}

/**
 * All history rows grouped by skill key, each oldest first. A plain object
 * (not a Map) so the store's structural equality can see changes.
 */
export function levelHistoryBySkill(s: AuroraState): Record<string, LevelPoint[]> {
  const out: Record<string, LevelPoint[]> = {}
  for (const h of forChild(s, s.levelHistory) as any[]) {
    const key = h.skillKey ?? h.skill_key
    ;(out[key] ??= []).push({ at: h.at, level: h.level, source: h.source, note: h.note })
  }
  for (const arr of Object.values(out)) arr.sort((a, b) => a.at - b.at)
  return out
}

export interface DomainSeries {
  key: string
  name: string
  /** Sampled at every date any skill in the domain moved. */
  points: Array<{ at: number; level: number }>
}

/**
 * Per-domain level over time, from skill history: at each movement date the
 * domain's value is the mean of the latest known level of every skill that has
 * ever been observed in it. This is the same number the "where she is" bar
 * shows, followed backwards, so the chart and the bar agree.
 */
export function domainSeries(s: AuroraState): DomainSeries[] {
  const byKey = levelHistoryBySkill(s)
  if (Object.keys(byKey).length === 0) return []
  const skillDomain = skillDomainKeys(s)
  const names = new Map<string, string>()
  for (const d of rubricDomains(s)) names.set(d.key, d.name)

  // Group skills by domain, collect all event times per domain.
  const perDomain = new Map<string, Map<string, LevelPoint[]>>()
  for (const [key, pts] of Object.entries(byKey)) {
    const dk = skillDomain[key] ?? key.split('.')[0] ?? 'other'
    if (!perDomain.has(dk)) perDomain.set(dk, new Map())
    perDomain.get(dk)!.set(key, pts)
  }

  const out: DomainSeries[] = []
  for (const [dk, skills] of perDomain) {
    const times = [...new Set([...skills.values()].flat().map((p) => p.at))].sort((a, b) => a - b)
    const points = times.map((t) => {
      let sum = 0, n = 0
      for (const pts of skills.values()) {
        let latest: number | undefined
        for (const p of pts) { if (p.at <= t) latest = p.level; else break }
        if (latest !== undefined) { sum += latest; n++ }
      }
      return { at: t, level: n ? sum / n : 0 }
    })
    out.push({ key: dk, name: names.get(dk) ?? dk, points })
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

export interface FocusTarget {
  skillKey: string
  targetLevel?: number
  byDate?: string
  goal: string
  domainKey?: string
}

/** The active cycle's target skills, with target level and date — the projection. */
export function focusTargets(s: AuroraState): FocusTarget[] {
  const cycle: any = activeCycle(s)
  if (!cycle) return []
  const goals: any[] = cycle.goals ?? []
  const out: FocusTarget[] = []
  for (const g of goals) {
    for (const key of g.targetSkillKeys ?? g.target_skill_keys ?? []) {
      out.push({
        skillKey: key,
        targetLevel: g.targetLevel ?? g.target_level,
        byDate: cycle.endDate ?? cycle.end_date,
        goal: g.statement,
        domainKey: g.domainKey ?? g.domain_key,
      })
    }
  }
  return out
}

export interface Review {
  _id: string
  period: 'day' | 'week' | 'cycle'
  date: string
  weekId?: string
  cycleId?: string
  status: string
  parent?: {
    ratings?: Array<{ domainKey: string; rating: number }>
    answers?: Array<{ question: string; answer: string }>
    win?: string
    adjust?: string
    notes?: string
    submittedAt?: number
  }
  agent?: {
    summary: string
    moved?: string[]
    stuck?: string[]
    changes?: string[]
    generatedAt: number
  }
}

export function reviews(s: AuroraState): Review[] {
  return (forChild(s, s.reviews) as any[])
    .map(normalizeReview)
    .sort((a, b) => b.date.localeCompare(a.date))
}

function normalizeReview(r: any): Review {
  const p = r.parent
  const a = r.agent
  return {
    _id: r._id,
    period: r.period,
    date: r.date,
    weekId: r.weekId ?? r.week_id,
    cycleId: r.cycleId ?? r.cycle_id,
    status: r.status,
    parent: p
      ? {
          ratings: (p.ratings ?? []).map((x: any) => ({ domainKey: x.domainKey ?? x.domain_key, rating: x.rating })),
          answers: p.answers,
          win: p.win,
          adjust: p.adjust,
          notes: p.notes,
          submittedAt: p.submittedAt ?? p.submitted_at,
        }
      : undefined,
    agent: a
      ? { summary: a.summary, moved: a.moved, stuck: a.stuck, changes: a.changes, generatedAt: a.generatedAt ?? a.generated_at }
      : undefined,
  }
}

export function reviewFor(s: AuroraState, period: Review['period'], date: string): Review | undefined {
  const r = (forChild(s, s.reviews) as any[]).find((x) => x.period === period && x.date === date)
  return r ? normalizeReview(r) : undefined
}

/** ISO Monday on or before `date`. */
export function mondayOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  const dow = (d.getUTCDay() + 6) % 7
  return new Date(d.getTime() - dow * 86_400_000).toISOString().slice(0, 10)
}

export function addDays(date: string, n: number): string {
  return new Date(new Date(`${date}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10)
}

/** The day row for a date (spine). */
export function dayRow(s: AuroraState, date: string): any | undefined {
  return (forChild(s, s.days) as any[]).find((d) => d.date === date)
}

/** Blocks for a whole week (Monday..Sunday), keyed by date. */
export function weekBlocks(s: AuroraState, monday: string): Array<{ date: string; blocks: PlanBlock[] }> {
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(monday, i)
    return { date, blocks: blocksForDate(s, date) }
  })
}

/**
 * Which week check-in is due: the current week from Sunday on, or last week
 * until it has been submitted. Undefined when nothing is due.
 */
export function dueWeekCheckin(s: AuroraState, today: string): { monday: string; week?: PlanWeek } | undefined {
  const dow = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7 // 0 = Monday
  const thisMonday = mondayOf(today)
  const lastMonday = addDays(thisMonday, -7)
  const candidates = dow === 6 ? [thisMonday, lastMonday] : [lastMonday]
  for (const monday of candidates) {
    const week = planWeeks(s).find((w) => w.startDate && mondayOf(w.startDate) === monday)
    if (!week) continue
    const hadBlocks = weekBlocks(s, monday).some((d) => d.blocks.length > 0)
    if (!hadBlocks) continue
    const r = reviewFor(s, 'week', monday)
    if (r?.parent?.submittedAt) continue
    return { monday, week }
  }
  return undefined
}
